import {
  InvalidPhoneVerificationCodeLengthError,
  NoMagicLinkTokenError,
  NoPhoneVerificationCodeError,
  PhoneNumberIsRequiredForPhoneVerificationError,
} from '~/errors/errors';
import { authGateway, AuthResponse, LoginCredentials, LoginRequest, OtpVerificationCodeRequest } from '~/gateways/auth/authGateway';
import { authTokenStorage } from '~/gateways/auth/authTokenStorage';
import { LoginType } from '~/types/LoginType';
import { GetCompanyInfoUseCase } from '../getCompanyInfoUseCase';
import { InitializeAppUseCase } from '../initializeAppUseCase';
import { MagicUseCase } from '../magicUseCase';

export class LogInUseCase extends MagicUseCase {
  protected async runLogic(loginRequest: LoginRequest) {
    this.getState().user.loginInfo.error = undefined;
    try {
      if (!this.getState().user.companyInfo?.id) {
        await new GetCompanyInfoUseCase().execute(loginRequest.domain);
      }
      const companyId = this.getState().user.companyInfo?.id;
      let response: AuthResponse | undefined;
      if (loginRequest.loginType === LoginType.Account) {
        const loginInfo = {
          email: loginRequest.email,
          password: loginRequest.password,
          companyId,
        } as LoginCredentials;
        response = await authGateway.authenticate(loginInfo);
      } else if (loginRequest.loginType === LoginType.MagicLink) {
        if (!loginRequest.loginToken) {
          throw new NoMagicLinkTokenError();
        }
        response = await authGateway.verifyMagicLink(loginRequest.loginToken);
      } else if (loginRequest.loginType === LoginType.PhoneNumber) {
        if (!loginRequest.loginToken) {
          throw new NoPhoneVerificationCodeError();
        }
        if (loginRequest.loginToken?.length !== 6) {
          throw new InvalidPhoneVerificationCodeLengthError();
        }
        if (!loginRequest.phone) {
          throw new PhoneNumberIsRequiredForPhoneVerificationError();
        }
        response = await authGateway.verifyOtp({
          phone: loginRequest.phone,
          code: loginRequest.loginToken,
        } as OtpVerificationCodeRequest);
      }
      this.getState().user.loginInfo.isLoggedIn = response?.success ?? false;
      if (response && response.success) {
        await authTokenStorage.setAuthToken(response.token);
        await new InitializeAppUseCase().execute(loginRequest.domain);
        this.navigate('/tenants');
      }
    } catch (error) {
      if (loginRequest.loginType === LoginType.MagicLink) {
        this.navigate('/tenants/auth/signin');
      }
      throw error;
    }
  }
}

export class LogInWithTokenUseCase extends MagicUseCase {
  protected async runLogic(token: string) {
    authTokenStorage.setAuthToken(token);
  }
}
